import React, { memo, useCallback, useEffect, useMemo } from "react";

import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useForm } from "react-hook-form";

import styled from "@emotion/styled";

import { Button } from "@/components/Buttons";
import Guide from "@/components/Guide";
import Table from "@/components/Table";

import DeliveryModal from "@/modals/DeliveryModal";

import {
  useAgPostalCodesStore,
  useDeliveriesCollectedStore,
  useLoadingStore,
  useModalStore,
  useProfileStore,
} from "@/stores/hooks";

const BodyContent = styled.div``;
const Container = styled.div`
  width: 100%;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: 40px;
`;

const SearchWrap = styled.div`
  padding-top: 15px;
`;
const SearchRow = styled(Row)`
  margin-bottom: 10px;
`;
const Column = styled(Col)`
  display: flex;
  align-items: center;
`;
const ColumnRight = styled(Column)`
  justify-content: flex-end;
`;
const SearchButton = styled(Button)`
  margin-left: 5px;
`;
const FormControl = styled(Form.Control)`
  width: ${({ width }) => width || 180}px !important;
`;
const Wave = styled.span`
  margin: 0 5px;
`;
const SearchLabel = styled.div`
  font-weight: bold;
  margin-right: 10px;
`;

const TableWrap = styled.div``;
const ColumnLink = styled.div`
  cursor: pointer;
  text-decoration: underline;
`;

const BookIdColumn = memo(({ bookId, prefix, onAfterSubmit }) => {
  const { openModal } = useModalStore();

  const handleClick = useCallback(async () => {
    openModal(
      <DeliveryModal
        key={`delivery-${bookId}-modal`}
        bookId={bookId}
        onAfterSubmit={onAfterSubmit}
      />,
    );
  }, [bookId, onAfterSubmit]);

  return (
    <ColumnLink onClick={handleClick}>
      {prefix}
      {bookId}
    </ColumnLink>
  );
});

export default function Deliveries() {
  const { agPostalCodes } = useAgPostalCodesStore().state;
  const { ...agPostalCodesActions } = useAgPostalCodesStore();

  const { state, ...actions } = useDeliveriesCollectedStore();
  const { startLoading, finishLoading } = useLoadingStore();

  const { user } = useProfileStore().state;
  const { ...profileActions } = useProfileStore();

  const { handleSubmit, reset } = useForm();

  const fetchAll = useCallback(async () => {
    try {
      startLoading();
      await actions.fetchAll({ isDrx: user.isDrx });
    } catch (_e) {}

    finishLoading();
  }, [actions.fetchAll, user.isDrx]);

  useEffect(() => {
    reset({
      status: state.query.status,
    });
  }, []);

  useEffect(() => {
    fetchAgPostalCodes();
    fetchUser();
  }, []);

  useEffect(() => {
    if (state.query.page > 1) {
      actions.setQuery({ page: 1 });
    } else {
      fetchAll();
    }
  }, [state.query.status]);

  useEffect(() => {
    fetchAll();
  }, [
    state.query.page,
    state.query.pageSize,
    state.query.sortKey,
    state.query.sortType,
  ]);

  const fetchAgPostalCodes = useCallback(async () => {
    if (!agPostalCodes.length) {
      try {
        await agPostalCodesActions.fetchAll();
      } catch (e) {
        console.log(e);
      }
    }
  }, [agPostalCodesActions.fetchAll, agPostalCodes]);

  const fetchUser = useCallback(async () => {
    try {
      await profileActions.fetchUser();
    } catch (e) {
      console.log(e);
    }
  }, []);

  const handleChange = useCallback(
    (e) => {
      actions.setQuery({ [e.target.name]: e.target.value });

      if (e.target.name === "dateFrom" && e.target.value > state.query.dateTo) {
        actions.setQuery({
          dateTo: e.target.value,
        });
      }

      if (e.target.name === "dateTo" && e.target.value < state.query.dateFrom) {
        actions.setQuery({
          dateFrom: e.target.value,
        });
      }
    },
    [actions.setQuery, state.query],
  );

  const handleClickHeader = useCallback(
    ({ sortKey, sortType }) => {
      actions.setQuery({ sortKey, sortType });
    },
    [actions.setQuery],
  );

  const handleGoToPage = useCallback(
    (page) => {
      actions.setQuery({ page });
    },
    [actions.setQuery],
  );

  const handleNextPage = useCallback(() => {
    actions.setQuery({ page: state.query.page + 1 });
  }, [actions.setQuery, state.query.page]);

  const handlePreviousPage = useCallback(() => {
    actions.setQuery({ page: state.query.page - 1 });
  }, [actions.setQuery, state.query.page]);

  const handleSetPageSize = useCallback(
    (pageSize) => {
      actions.setQuery({ page: 1, pageSize });
    },
    [actions.setQuery],
  );

  const handleSelect = useCallback(() => {}, []);

  const onSubmit = useCallback(() => {
    fetchAll();
  }, [fetchAll]);

  let _columns = [
    {
      Header: "사측주문번호",
      accessor: "deliveryObj.orderIdFromCorp",
      id: "orderIdFromCorp",
      sort: true,
      width: 110,
    },
    {
      Header: "예약번호",
      accessor: (row) => {
        return (
          <BookIdColumn
            bookId={row.bookId}
            prefix={row.deliveryObj?.renderBookIdPrefix()}
            onAfterSubmit={fetchAll}
          />
        );
      },
      width: 110,
    },
    {
      Header: "접수일시",
      accessor: (row) => row.deliveryObj?.renderReceiptDate(),
      id: "receiptDate",
      sort: true,
      width: 100,
    },
    {
      Header: "송하인명",
      accessor: "deliveryObj.senderName",
      width: 80,
    },
    {
      Header: "접수점",
      accessor: "deliveryObj.spotName",
      width: 80,
    },
    {
      Header: "고객명",
      accessor: "deliveryObj.receiverName",
      width: 80,
    },
  ];

  if (user?.showRequestedDeliveryTime) {
    _columns.push({
      Header: user.isCjoyQuick ? "픽업요청일시" : "배송요청일시",
      accessor: (row) => row.deliveryObj?.renderRequestedDeliveryTime(),
      id: "requestedDeliveryTime",
      sort: true,
      width: 100,
    });
  }

  _columns = [
    ..._columns,
    {
      Header: "상품명",
      accessor: "deliveryObj.productName",
      width: 100,
    },
    {
      Header: "기타사항1",
      accessor: "deliveryObj.etc1",
      width: 80,
    },
    {
      Header: "기타사항2",
      accessor: "deliveryObj.etc2",
      width: 80,
    },
    {
      Header: "기타사항3",
      accessor: "deliveryObj.etc3",
      width: 80,
    },
    {
      Header: "타택배사 운송장번호",
      accessor: "deliveryObj.agencyOrderId",
      width: 100,
    },
    {
      Header: "배송연기",
      accessor: (row) => row.deliveryObj?.renderPostponedDate(),
      width: 100,
    },
    {
      Header: "수거완료",
      accessor: (row) => row.deliveryObj?.renderPickupDateCompleted(),
      width: 100,
    },
    {
      Header: "배송중",
      accessor: (row) => row.deliveryObj?.renderReleasedAt(),
      width: 100,
    },

    {
      Header: "배송완료",
      accessor: (row) => row.deliveryObj?.renderDeliveryCompletedDate(),
      id: "deliveryCompletedDate",
      sort: true,
      width: 100,
    },
    {
      Header: "취소",
      accessor: (row) => row.deliveryObj?.renderCanceledAt(),
      width: 100,
    },
    {
      Header: "분실",
      accessor: (row) => row.deliveryObj?.renderLostDate(),
      width: 100,
    },
    {
      Header: "반송",
      accessor: (row) => row.deliveryObj?.renderSentBackDate(),
      width: 100,
    },
    {
      Header: "미배송목록",
      accessor: (row) => row.deliveryObj?.renderDelayedDeliveries(),
      width: 100,
    },
    {
      Header: "운송장출력여부",
      accessor: (row) => (row.deliveryObj?.printed ? "O" : "X"),
      width: 80,
    },
  ];

  const columns = useMemo(() => _columns, [fetchAll]);

  return (
    <BodyContent>
      <Guide />
      <Container>
        <SearchWrap>
          <SearchRow>
            <Column xs={8}>
              <Form inline onSubmit={handleSubmit(onSubmit)}>
                <FormControl
                  width={140}
                  as="select"
                  name="senderName"
                  onChange={handleChange}
                  custom
                >
                  <option value="">지점 전체</option>
                  {user?.senderNames?.map((el, index) => {
                    return (
                      <option value={el} key={index}>
                        {el}
                      </option>
                    );
                  })}
                </FormControl>
                <Wave></Wave>
                <FormControl
                  width={140}
                  as="select"
                  name="region"
                  onChange={handleChange}
                  custom
                >
                  <option value="">지역 전체</option>
                  <option value="SO_IC_GG_DJ_SJ_CJ">
                    서울/인천/경기/대전/세종/청주/원주
                  </option>
                  <option value="BUSAN_GYUNGNAM">부산/울산/경남</option>
                  <option value="GWANGJU_JEONNAM">광주/전남</option>
                  <option value="DAEGU_GYEONGSAN">대구/경산</option>
                  <option value="JEONBUK">전북</option>
                  <option value="GYEONGJU_POHANG">경주/포항</option>
                </FormControl>
                <Wave></Wave>
                <FormControl
                  name="dateFrom"
                  onChange={handleChange}
                  type="date"
                  value={state.query.dateFrom}
                />
                <Wave>~</Wave>
                <FormControl
                  name="dateTo"
                  onChange={handleChange}
                  type="date"
                  value={state.query.dateTo}
                />
                <SearchButton type="submit" width="100px">
                  조회
                </SearchButton>
              </Form>
            </Column>
            <ColumnRight xs={4}>
              <Form inline onSubmit={handleSubmit(onSubmit)}>
                <FormControl
                  width={260}
                  name="bookIdOrOrderIdFromCorp"
                  onChange={handleChange}
                  placeholder="예약번호, 사측주문번호 조회"
                  value={state.query.bookIdOrOrderIdFromCorp}
                />
                <SearchButton type="submit" width="100px">
                  조회
                </SearchButton>
              </Form>
            </ColumnRight>
          </SearchRow>
          <SearchRow>
            <Column xs={7}></Column>
            <ColumnRight xs={5}>
              <SearchLabel>전체 총 {state.totalCount} 조회</SearchLabel>
            </ColumnRight>
          </SearchRow>
        </SearchWrap>
        <TableWrap>
          <Table
            responsive
            bordered
            hover
            columns={columns}
            currentPage={state.query.page}
            data={state.deliveriesCollected}
            goToPage={handleGoToPage}
            nextPage={handleNextPage}
            onClickHeader={handleClickHeader}
            onSelect={handleSelect}
            pageCount={state.pageCount}
            pageSize={state.query.pageSize}
            previousPage={handlePreviousPage}
            setPageSize={handleSetPageSize}
          />
        </TableWrap>
      </Container>
    </BodyContent>
  );
}
